/**
 * Gift cards — the manual-review rail, and the catalogue behind it.
 *
 * Two surfaces on one page, and — this is the thing to get right — **two different
 * grants**, not one:
 *
 *  1. **Review queue** — the pending redemptions a customer submitted. Opening one loads
 *     the reviewer-only detail (the decrypted card code and, if supplied, the card image
 *     — both bearer secrets the API serves only to an admin). *Approve* posts the balanced
 *     ledger entry that credits the customer's naira wallet; *reject* records a
 *     safe-to-show reason and moves no money. Gated on `kyc:review` — **compliance**.
 *  2. **Brands** — the catalogue the customer app reads: each brand's naira-per-unit rate,
 *     face bounds, and whether it is live. Gated on `rates:write` — **finance**.
 *
 * The tab layout is a console convenience and is not a reason to hand pricing to the
 * compliance desk: a brand's rate is the multiplier every future payout for that brand runs
 * through, which is the same act `rates:write` exists to gate on the FX board. So the API
 * gates the two halves separately, and **the catalogue's read is gated as tightly as its
 * writes** — a rate is the desk's margin and there is no support question that needs it.
 *
 * That last detail is why neither tab below simply disables its buttons. A role without the
 * grant is refused on the *list*, so each tab checks first and explains instead of firing a
 * request it knows will 403 and painting an error box over a page that is working correctly.
 *
 * The whole rail is dark to customers until an operator flips the `gift_cards` flag on
 * (Feature flags); this console reviews and prices whatever exists regardless.
 */
import { useRef, useState } from 'react';

import { API_ORIGIN, USING_MOCK } from '../api/config.js';
import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import { adminCan } from '../contracts/permissions.js';
import { API_PREFIX } from '../contracts/routes.js';
import type {
  AdminGiftcardBrand,
  AdminRedemptionDetail,
  AdminRedemptionQueueItem,
  CreateGiftcardBrandRequest,
  FaceMoneyDTO,
  GiftcardCategory,
  GiftcardFaceCurrency,
  UpdateGiftcardBrandRequest,
} from '../contracts/types.js';
import { GIFTCARD_CATEGORIES, GIFTCARD_FACE_CURRENCIES } from '../contracts/vocab.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorBox,
  Field,
  Input,
  LoadingState,
  Select,
  Switch,
  Textarea,
} from '../components/ui.js';
import { GiftcardStatusBadge } from '../components/badges.js';
import { IconAlert } from '../components/icons.js';
import { Modal } from '../components/Modal.js';
import { useToast } from '../components/Toast.js';
import { useAsync } from '../hooks/useAsync.js';
import { humanize, relativeTime } from '../lib/format.js';
import {
  faceDecimalsOf,
  formatFaceMoney,
  formatGiftcardRate,
  formatMoney,
  minorUnitsToInput,
  parseMinorUnits,
} from '../lib/money.js';
import {
  GIFTCARD_RATE_MAX_CHANGE_FACTOR,
  giftcardPayoutKobo,
  giftcardRateChangeIsSane,
  giftcardRateRational,
  giftcardRateToInput,
  parseRateInput,
} from '../lib/rate.js';

type Tab = 'queue' | 'brands';

export function GiftcardsPage(): JSX.Element {
  const { session } = useAuth();
  const role = session?.me.role ?? 'support';
  const canReview = adminCan(role, 'kyc:review');
  const canPrice = adminCan(role, 'rates:write');

  // Open on a tab this role can actually use. A compliance operator landing on Brands —
  // the one tab their token is refused on — would read the console as broken rather than
  // as correctly scoped.
  const [tab, setTab] = useState<Tab>(canReview ? 'queue' : 'brands');

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">Gift cards</h1>
        <p className="page-head__sub">
          Two jobs here. Compliance reviews redemptions, and approving one credits the customer's naira. Finance sets each brand's rate, which every future payout for that brand uses.
        </p>
      </div>

      <div className="toolbar" role="tablist">
        <Button variant={tab === 'queue' ? 'primary' : 'default'} onClick={() => setTab('queue')}>
          Review queue
        </Button>
        <Button variant={tab === 'brands' ? 'primary' : 'default'} onClick={() => setTab('brands')}>
          Brands
        </Button>
      </div>

      {tab === 'queue' ? <QueueTab canReview={canReview} /> : <BrandsTab canPrice={canPrice} />}
    </>
  );
}

/**
 * A tab a role's token is refused on, said plainly.
 *
 * Not an error box: nothing has gone wrong. The alternative — firing the request anyway and
 * rendering the 403 — trains operators to read a correct permission boundary as an outage.
 */
function NoGrant({ title, body }: { title: string; body: string }): JSX.Element {
  return (
    <Card title={title}>
      <p className="muted" style={{ marginTop: 0 }}>
        {body}
      </p>
    </Card>
  );
}

function QueueTab({ canReview }: { canReview: boolean }): JSX.Element {
  const { api } = useAuth();
  // The queue read itself is compliance-only, so a finance operator must not be sent at it.
  const state = useAsync(
    () => (canReview ? api.giftcardQueue() : Promise.resolve({ items: [], nextCursor: null })),
    [canReview],
  );
  const [openId, setOpenId] = useState<string | null>(null);

  const items = state.data?.items ?? [];

  function drop(id: string) {
    state.setData((prev) => (prev === null ? prev : { ...prev, items: prev.items.filter((i) => i.id !== id) }));
    setOpenId(null);
  }

  if (!canReview) {
    return (
      <NoGrant
        title="Redemption review is a compliance grant"
        body="Deciding a redemption is a judgement about whether a card is genuine, so the API serves this queue, and the card codes in it, only to compliance and super admins. Your role can set brand rates on the Brands tab."
      />
    );
  }

  return (
    <>
      <Card
        flush
        title="Pending redemptions"
        actions={<Badge tone={items.length > 0 ? 'amber' : 'green'}>{items.length} pending</Badge>}
      >
        {state.loading ? (
          <LoadingState label="Loading queue…" />
        ) : state.error !== null ? (
          <ErrorBox message={state.error} />
        ) : items.length === 0 ? (
          <EmptyState glyph="✓" title="Queue is clear" hint="No gift-card redemptions are waiting." />
        ) : (
          <div className="table-wrap">
            <table className="table table--rows-clickable">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Brand</th>
                  <th className="table__num">Face value</th>
                  <th className="table__num">Qty</th>
                  <th className="table__num">Payout</th>
                  <th>Status</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item: AdminRedemptionQueueItem) => (
                  <tr key={item.id} onClick={() => setOpenId(item.id)}>
                    <td className="mono">{item.maskedPhone}</td>
                    <td style={{ fontWeight: 600 }}>{item.brandName}</td>
                    <td className="table__num">{formatFaceMoney(item.faceValue)}</td>
                    <td className="table__num">{item.quantity}</td>
                    <td className="table__num" style={{ fontWeight: 650 }}>{formatMoney(item.payoutAmount)}</td>
                    <td>
                      <GiftcardStatusBadge status={item.status} />
                    </td>
                    <td className="muted" title={item.createdAt}>{relativeTime(item.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {openId !== null && (
        <RedemptionDetailModal
          id={openId}
          canReview={canReview}
          onResolved={drop}
          onClose={() => setOpenId(null)}
        />
      )}
    </>
  );
}

function RedemptionDetailModal({
  id,
  canReview,
  onResolved,
  onClose,
}: {
  id: string;
  canReview: boolean;
  onResolved: (id: string) => void;
  onClose: () => void;
}): JSX.Element {
  const { api } = useAuth();
  const toast = useToast();
  const state = useAsync(() => api.giftcardItem(id), [id]);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const detail = state.data;

  async function approve() {
    setBusy(true);
    try {
      await api.reviewGiftcard(id, { decision: 'approve' });
      toast.success('Redemption approved', 'The customer’s naira wallet has been credited.');
      onResolved(id);
    } catch (err) {
      toast.error('Could not approve', messageOf(err));
    } finally {
      setBusy(false);
    }
  }

  async function reject() {
    setBusy(true);
    try {
      await api.reviewGiftcard(id, { decision: 'reject', reason: reason.trim() || 'Card could not be verified.' });
      toast.success('Redemption rejected');
      onResolved(id);
    } catch (err) {
      toast.error('Could not reject', messageOf(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title="Review redemption"
      description="Inspect the card, then approve to credit the customer or reject with a reason."
      onClose={onClose}
      footer={
        canReview && detail !== null && detail.status === 'pending' ? (
          rejecting ? (
            <>
              <Button variant="ghost" onClick={() => setRejecting(false)} disabled={busy}>
                Back
              </Button>
              <Button variant="danger" loading={busy} onClick={reject}>
                Confirm rejection
              </Button>
            </>
          ) : (
            <>
              <Button variant="danger" disabled={busy} onClick={() => setRejecting(true)}>
                Reject
              </Button>
              <Button variant="accent" loading={busy} onClick={approve}>
                Approve &amp; credit
              </Button>
            </>
          )
        ) : (
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        )
      }
    >
      {state.loading ? (
        <LoadingState label="Loading card…" />
      ) : state.error !== null ? (
        <ErrorBox message={state.error} />
      ) : detail === null ? (
        <ErrorBox message="This redemption is no longer available." />
      ) : (
        <>
          <div className="kv">
            <dt>Customer</dt>
            <dd className="mono">{detail.maskedPhone}</dd>
            <dt>Brand</dt>
            <dd style={{ fontWeight: 600 }}>{detail.brandName}</dd>
            <dt>Face value</dt>
            <dd>
              {formatFaceMoney(detail.faceValue)} <span className="faint">× {detail.quantity}</span>
            </dd>
            <dt>Payout</dt>
            <dd style={{ fontWeight: 700 }}>{formatMoney(detail.payoutAmount)}</dd>
            <dt>Status</dt>
            <dd><GiftcardStatusBadge status={detail.status} /></dd>
            <dt>Submitted</dt>
            <dd>{relativeTime(detail.createdAt)}</dd>
          </div>

          {/*
            Every code on the submission, one per line and numbered.

            A submission may carry up to five cards, and reviewing one of five while approving
            the payout for all five is the mistake this exists to make impossible. The numbers
            match the order the customer typed them, which is the only thing tying "the third
            one did not work" in a support message to a specific card here.

            `codes` falls back to the single `cardCode` for a redemption written before the
            codes table existed, so an old one reads exactly as it always did.
          */}
          <div className="field" style={{ marginTop: 16 }}>
            <div className="field__label">
              {reviewerCodes(detail).length === 1
                ? 'Card code (reviewer only)'
                : `${String(reviewerCodes(detail).length)} card codes (reviewer only)`}
            </div>
            {reviewerCodes(detail).map((code, index, all) => (
              <div className="gc-code-row" key={`${String(index)}-${code.slice(-4)}`}>
                {all.length > 1 && <span className="gc-code-row__n">{index + 1}</span>}
                <span className="gc-secret mono">{code}</span>
              </div>
            ))}
            {reviewerCodes(detail).length > 1 && (
              <p className="muted" style={{ marginTop: 8, fontSize: 12 }}>
                Check every one before you approve. The payout above covers all{' '}
                {reviewerCodes(detail).length}.
              </p>
            )}
          </div>

          {detail.image !== null && (
            <div className="field" style={{ marginTop: 16 }}>
              <div className="field__label">Card image (reviewer only)</div>
              <div className="gc-image">
                <img
                  src={`data:${detail.imageMime ?? 'image/png'};base64,${detail.image}`}
                  alt="Submitted gift card"
                />
              </div>
            </div>
          )}

          {detail.reason !== null && detail.status === 'rejected' && (
            <div className="error-box" style={{ marginTop: 16 }}>
              <span>Rejected: {detail.reason}</span>
            </div>
          )}

          {rejecting && (
            <Field label="Reason (shown to the customer)">
              <Textarea
                value={reason}
                maxLength={200}
                placeholder="e.g. Card balance already redeemed elsewhere"
                onChange={(e) => setReason(e.target.value)}
              />
            </Field>
          )}
        </>
      )}
    </Modal>
  );
}

// ── Brands ─────────────────────────────────────────────────────────────────────────

function BrandsTab({ canPrice }: { canPrice: boolean }): JSX.Element {
  const { api } = useAuth();
  const toast = useToast();
  // The list is gated on the same grant as the writes, so a role without it is refused here
  // too — the request is not made at all rather than made and error-boxed.
  const state = useAsync(
    () => (canPrice ? api.listGiftcardBrandsAdmin() : Promise.resolve({ brands: [] })),
    [canPrice],
  );
  /** `null` = closed, `'new'` = the create form, otherwise the brand being edited. */
  const [editing, setEditing] = useState<AdminGiftcardBrand | 'new' | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [uploadingId, setUploadingId] = useState<string | null>(null);

  const brands = state.data?.brands ?? [];

  function replace(updated: AdminGiftcardBrand): void {
    state.setData((prev) =>
      prev === null
        ? prev
        : { brands: prev.brands.map((b) => (b.id === updated.id ? updated : b)) },
    );
  }

  /**
   * Flip a brand on or off.
   *
   * Sends `active` alone. That is not tidiness: a full-object write would re-send the rate,
   * and the API's deviation guard measures a submitted rate against the standing one — so a
   * round-trip through the console's eight-decimal rendering could, on an awkward rational,
   * turn "switch this brand on" into a rate change nobody asked for. A patch of one field
   * cannot.
   */
  /**
   * Set a brand's icon.
   *
   * Both refusals are made here rather than left to the server, not because the server does
   * not make them - it decodes the bytes and checks the magic number - but because a rejected
   * upload that only fails after a megabyte has crossed the wire is a worse experience than one
   * refused instantly, and the messages here can name the actual limits.
   */
  async function uploadIcon(brand: AdminGiftcardBrand, file: File | undefined): Promise<void> {
    if (file === undefined) return;
    if (!ICON_MIMES.includes(file.type)) {
      toast.error('That file will not work', 'Choose a PNG, JPG or WebP image.');
      return;
    }
    if (file.size > ICON_MAX_BYTES) {
      toast.error('That image is too big', 'Keep a brand icon under 200 KB.');
      return;
    }

    setUploadingId(brand.id);
    try {
      const dataBase64 = await fileToBase64(file);
      const updated = await api.uploadGiftcardBrandLogo(brand.id, {
        mime: file.type as 'image/png' | 'image/jpeg' | 'image/webp',
        dataBase64,
      });
      replace(updated);
      toast.success('Icon set', `${updated.name} now shows its own mark in the app.`);
    } catch (err) {
      toast.error('That icon was not saved', messageOf(err));
    } finally {
      setUploadingId(null);
    }
  }

  async function toggleActive(brand: AdminGiftcardBrand, active: boolean): Promise<void> {
    setTogglingId(brand.id);
    try {
      const updated = await api.updateGiftcardBrand(brand.id, { active });
      replace(updated);
      toast.success(
        `${updated.name} is ${active ? 'live' : 'off'}`,
        active
          ? 'Customers can submit this brand again.'
          : 'It stays in this list, and customers no longer see it.',
      );
    } catch (err) {
      toast.error('Could not change that brand', messageOf(err));
      // The write did not land, so the switch has to go back to what the server still holds.
      state.reload();
    } finally {
      setTogglingId(null);
    }
  }

  if (!canPrice) {
    return (
      <NoGrant
        title="Brand rates are a finance grant"
        body="A brand’s rate is the multiplier every future payout for that brand runs through. It is a treasury decision, gated on the same grant as the FX board. The API serves this catalogue only to finance and super admins, so it is not read here at all rather than fetched and refused."
      />
    );
  }

  return (
    <>
      <Card
        flush
        title="Brand catalogue"
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Badge tone="neutral">{brands.length} brands</Badge>
            <Button size="sm" variant="primary" onClick={() => setEditing('new')}>
              New brand
            </Button>
          </div>
        }
      >
        {state.loading ? (
          <LoadingState label="Loading brands…" />
        ) : state.error !== null ? (
          <ErrorBox message={state.error} />
        ) : brands.length === 0 ? (
          <EmptyState
            glyph="🎁"
            title="No brands yet"
            hint="Add one to open gift-card redemption. A customer can only submit a card for a brand that exists here."
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Icon</th>
                  <th>Brand</th>
                  <th>Category</th>
                  <th className="table__num">Rate</th>
                  <th className="table__num">Pays out</th>
                  <th className="table__num">Face range</th>
                  <th>Live</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {brands.map((brand) => (
                  <BrandRow
                    key={brand.id}
                    brand={brand}
                    busy={togglingId === brand.id || uploadingId === brand.id}
                    canWrite={canPrice}
                    onToggle={(active) => void toggleActive(brand, active)}
                    onEdit={() => setEditing(brand)}
                    onUpload={(file) => void uploadIcon(brand, file)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing !== null && (
        <BrandEditor
          brand={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(saved, created) => {
            setEditing(null);
            if (created) state.reload();
            else replace(saved);
          }}
        />
      )}
    </>
  );
}

function BrandRow({
  brand,
  busy,
  canWrite,
  onToggle,
  onEdit,
  onUpload,
}: {
  brand: AdminGiftcardBrand;
  busy: boolean;
  canWrite: boolean;
  onToggle: (active: boolean) => void;
  onEdit: () => void;
  onUpload: (file: File | undefined) => void;
}): JSX.Element {
  const decimals = faceDecimalsOf(brand.faceCurrency);
  const rate = {
    numerator: BigInt(brand.rateNairaPerUnit.numerator),
    denominator: BigInt(brand.rateNairaPerUnit.denominator),
  };
  const payoutAtMin = giftcardPayoutKobo({
    rate,
    faceMinor: BigInt(brand.minFace.amount),
    faceDecimals: decimals,
    quantity: 1n,
  });
  const payoutAtMax = giftcardPayoutKobo({
    rate,
    faceMinor: BigInt(brand.maxFace.amount),
    faceDecimals: decimals,
    quantity: 1n,
  });

  return (
    <tr className={brand.active ? undefined : 'gcb-row--off'}>
      {/*
        The icon, first, because it is what an operator scans the list by and it is the one
        column that tells them at a glance which brands are still unfinished. The app draws
        this beside every brand in the catalogue and on every past redemption in a customer's
        history, so a brand without one is visibly half-configured everywhere it appears.
      */}
      <td>
        <BrandIconCell brand={brand} canWrite={canWrite} busy={busy} onUpload={onUpload} />
      </td>
      <td>
        <span style={{ fontWeight: 600 }}>{brand.name}</span>{' '}
        <span className="faint mono">{brand.country}</span>
      </td>
      <td style={{ textTransform: 'capitalize' }}>{humanize(brand.category)}</td>
      {/* Grouped and read-only here — `formatGiftcardRate` renders "₦1,200.00 per $1", which
          is the sentence an operator would say. The ungrouped `giftcardRateToInput` is for
          the edit field, where separators are something the parser has to strip again. */}
      <td className="table__num mono">{formatGiftcardRate(brand.rateNairaPerUnit, brand.faceCurrency)}</td>
      {/* The rate is a ratio; this is the money. An operator judging a rate is judging what
          it pays, so the payout at the brand's own bounds sits beside it rather than
          being left as arithmetic to do in your head. */}
      <td className="table__num">
        {formatMoney({ currency: 'NGN', amount: payoutAtMin.toString() })}
        <span className="faint"> – </span>
        {formatMoney({ currency: 'NGN', amount: payoutAtMax.toString() })}
      </td>
      <td className="table__num">
        {formatFaceMoney(brand.minFace)} <span className="faint">–</span> {formatFaceMoney(brand.maxFace)}
      </td>
      <td>
        <Switch
          checked={brand.active}
          disabled={busy}
          label={`${brand.active ? 'Switch off' : 'Switch on'} ${brand.name}`}
          onChange={onToggle}
        />
      </td>
      <td>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button size="sm" disabled={busy} onClick={onEdit}>
            Edit
          </Button>
        </div>
      </td>
    </tr>
  );
}

/** A brand's editable state as the form holds it — every field a string the operator typed. */
interface BrandDraft {
  name: string;
  category: GiftcardCategory;
  country: string;
  faceCurrency: GiftcardFaceCurrency;
  /** Naira per one whole unit of face value, as typed. Never a number, at any point. */
  rate: string;
  /** Face bounds in *whole* units of the face currency — `50`, not `5000`. */
  minFace: string;
  maxFace: string;
  sortOrder: string;
  active: boolean;
}

function draftOf(brand: AdminGiftcardBrand | null): BrandDraft {
  if (brand === null) {
    return {
      name: '',
      category: 'shopping',
      country: 'US',
      faceCurrency: 'USD',
      rate: '',
      minFace: '',
      maxFace: '',
      sortOrder: '0',
      active: true,
    };
  }
  return {
    name: brand.name,
    category: brand.category,
    country: brand.country,
    faceCurrency: brand.faceCurrency,
    rate: giftcardRateToInput(brand.rateNairaPerUnit),
    minFace: minorUnitsToInput(brand.minFace.amount, faceDecimalsOf(brand.faceCurrency)),
    maxFace: minorUnitsToInput(brand.maxFace.amount, faceDecimalsOf(brand.faceCurrency)),
    sortOrder: String(brand.sortOrder),
    active: brand.active,
  };
}

/**
 * Create or edit one brand.
 *
 * Two things this form does that a plain set of inputs would not, both because the value
 * being edited is a payout multiplier rather than a setting:
 *
 * - **It shows the money, not the ratio.** As the rate is typed, the payout for the face
 *   bounds in the form is recomputed in `BigInt` by the same arithmetic the API uses, so an
 *   operator setting ₦1,200 per dollar sees ₦60,000 on a $50 card rather than being asked to
 *   trust a number they cannot check.
 * - **It refuses a ten-fold move before the API does.** An edit whose rate is 10× or more
 *   away from the brand's standing one is what a misplaced decimal point looks like, and the
 *   API refuses it. Blocking it here means the operator is told *why*, next to the field,
 *   rather than after a round-trip.
 *
 * An edit sends a sparse patch — only the fields that actually changed — which is the API's
 * PATCH semantics and, in particular, keeps an untouched rate out of the request entirely.
 */
function BrandEditor({
  brand,
  onClose,
  onSaved,
}: {
  brand: AdminGiftcardBrand | null;
  onClose: () => void;
  onSaved: (saved: AdminGiftcardBrand, created: boolean) => void;
}): JSX.Element {
  const { api } = useAuth();
  const toast = useToast();
  const [draft, setDraft] = useState<BrandDraft>(() => draftOf(brand));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const creating = brand === null;
  const decimals = faceDecimalsOf(draft.faceCurrency);
  const set = <K extends keyof BrandDraft>(key: K, value: BrandDraft[K]): void =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const rateCheck = parseRateInput(draft.rate);
  const rational = rateCheck.ok ? giftcardRateRational(rateCheck.price) : null;
  const minMinor = parseMinorUnits(draft.minFace, decimals);
  const maxMinor = parseMinorUnits(draft.maxFace, decimals);

  /** The standing rate an edit is measured against. Null on a create — nothing to anchor to. */
  const standing =
    brand === null
      ? null
      : {
          numerator: BigInt(brand.rateNairaPerUnit.numerator),
          denominator: BigInt(brand.rateNairaPerUnit.denominator),
        };
  const rateMovedTooFar =
    standing !== null && rational !== null && !giftcardRateChangeIsSane(standing, rational);

  const payoutAt = (faceMinor: bigint | null): string =>
    rational === null || faceMinor === null
      ? '–'
      : formatMoney({
          currency: 'NGN',
          amount: giftcardPayoutKobo({ rate: rational, faceMinor, faceDecimals: decimals, quantity: 1n }).toString(),
        });

  function validate(): string | null {
    if (draft.name.trim() === '') return 'Give the brand a name.';
    if (!/^[A-Z]{2}$/.test(draft.country.trim())) return 'Country must be a two-letter code, e.g. US or GB.';
    if (!rateCheck.ok) return rateCheck.message;
    if (minMinor === null || minMinor <= 0n) return 'Enter a smallest face value above zero.';
    if (maxMinor === null || maxMinor <= 0n) return 'Enter a largest face value above zero.';
    if (minMinor > maxMinor) return 'The largest face value cannot be below the smallest.';
    const sortOrder = Number(draft.sortOrder);
    if (!Number.isInteger(sortOrder) || sortOrder < 0 || sortOrder > 10_000) {
      return 'Display order must be a whole number between 0 and 10,000.';
    }
    if (rateMovedTooFar) {
      return (
        `That rate is ${GIFTCARD_RATE_MAX_CHANGE_FACTOR}× or more away from this brand’s current ` +
        `₦${giftcardRateToInput(brand!.rateNairaPerUnit)} per ${brand!.faceCurrency}, which is what a ` +
        'misplaced decimal point looks like. The API refuses it. Re-check the figure, or, if the ' +
        'move is genuinely intended, make it in two smaller steps.'
      );
    }
    return null;
  }

  async function save(): Promise<void> {
    const problem = validate();
    if (problem !== null) {
      setError(problem);
      return;
    }
    setError(null);
    setSaving(true);

    const face = (minor: bigint): FaceMoneyDTO => ({
      currency: draft.faceCurrency,
      amount: minor.toString(),
    });

    try {
      if (creating) {
        const body: CreateGiftcardBrandRequest = {
          name: draft.name.trim(),
          category: draft.category,
          country: draft.country.trim(),
          faceCurrency: draft.faceCurrency,
          // The literal characters the operator typed. The API turns them into the exact
          // rational it stores; nothing here converts the value first.
          rateNairaPerUnit: rateCheck.ok ? rateCheck.price : draft.rate,
          minFace: face(minMinor!),
          maxFace: face(maxMinor!),
          active: draft.active,
          sortOrder: Number(draft.sortOrder),
        };
        const saved = await api.createGiftcardBrand(body);
        toast.success(`${saved.name} added`, 'Customers can submit this brand once the gift-card flag is on.');
        onSaved(saved, true);
        return;
      }

      // A sparse patch: only what actually changed. Absent fields are left alone by the API,
      // and keeping an unchanged rate out of the body keeps it away from the deviation guard.
      const original = draftOf(brand);
      const body: UpdateGiftcardBrandRequest = {
        ...(draft.name.trim() !== original.name ? { name: draft.name.trim() } : {}),
        ...(draft.category !== original.category ? { category: draft.category } : {}),
        ...(draft.country.trim() !== original.country ? { country: draft.country.trim() } : {}),
        ...(draft.faceCurrency !== original.faceCurrency ? { faceCurrency: draft.faceCurrency } : {}),
        ...(draft.rate !== original.rate ? { rateNairaPerUnit: rateCheck.ok ? rateCheck.price : draft.rate } : {}),
        // The face bounds ride along whenever the currency moved, because their currency has
        // to agree with the brand's and the API validates the merged result, not the patch.
        ...(draft.minFace !== original.minFace || draft.faceCurrency !== original.faceCurrency
          ? { minFace: face(minMinor!) }
          : {}),
        ...(draft.maxFace !== original.maxFace || draft.faceCurrency !== original.faceCurrency
          ? { maxFace: face(maxMinor!) }
          : {}),
        ...(draft.sortOrder !== original.sortOrder ? { sortOrder: Number(draft.sortOrder) } : {}),
        ...(draft.active !== original.active ? { active: draft.active } : {}),
      };
      if (Object.keys(body).length === 0) {
        onClose();
        return;
      }
      const saved = await api.updateGiftcardBrand(brand.id, body);
      toast.success(
        `${saved.name} saved`,
        body.rateNairaPerUnit === undefined
          ? undefined
          : `Every payout for this brand now multiplies by ${formatGiftcardRate(saved.rateNairaPerUnit, saved.faceCurrency)}.`,
      );
      onSaved(saved, false);
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      title={creating ? 'New gift-card brand' : `Edit ${brand.name}`}
      description="The rate is naira per one whole unit of face value. Everything a customer is paid for this brand is that number multiplied by the card."
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          {/* Disabled on the one refusal the page is *certain* the API will make, so the
              red box beside the field and the button agree with each other. Every other
              validation stays on the click: those are typos an operator fixes in place,
              and a permanently dead Save button while a field is half-typed is worse. */}
          <Button
            variant="primary"
            loading={saving}
            disabled={rateMovedTooFar}
            onClick={() => void save()}
          >
            {creating ? 'Add brand' : 'Save brand'}
          </Button>
        </>
      }
    >
      <div className="grid grid--2" style={{ gap: 14 }}>
        <Field label="Name" htmlFor="gcb-name">
          <Input
            id="gcb-name"
            value={draft.name}
            maxLength={80}
            placeholder="e.g. Amazon US"
            onChange={(e) => set('name', e.target.value)}
          />
        </Field>
        <Field label="Country" htmlFor="gcb-country">
          <Input
            id="gcb-country"
            className="mono"
            value={draft.country}
            maxLength={2}
            placeholder="US"
            onChange={(e) => set('country', e.target.value.toUpperCase())}
          />
        </Field>
        <Field label="Category" htmlFor="gcb-category">
          <Select
            id="gcb-category"
            value={draft.category}
            onChange={(e) => set('category', e.target.value as GiftcardCategory)}
          >
            {GIFTCARD_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {humanize(c)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Face currency" htmlFor="gcb-currency">
          <Select
            id="gcb-currency"
            value={draft.faceCurrency}
            onChange={(e) => set('faceCurrency', e.target.value as GiftcardFaceCurrency)}
          >
            {GIFTCARD_FACE_CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <div className="field" style={{ marginTop: 4 }}>
        <label className="field__label" htmlFor="gcb-rate">
          Rate: naira per 1 {draft.faceCurrency} of face value
        </label>
        <Input
          id="gcb-rate"
          className="mono"
          inputMode="decimal"
          value={draft.rate}
          placeholder="1200.50"
          onChange={(e) => set('rate', e.target.value)}
        />
        <div className="gcb-payout">
          {rational === null ? (
            <span className="faint">Type a rate to see what it pays.</span>
          ) : (
            <>
              <span>
                Pays <strong>{payoutAt(minMinor)}</strong> for a{' '}
                {minMinor === null ? '–' : formatFaceMoney({ currency: draft.faceCurrency, amount: minMinor.toString() })}{' '}
                card
              </span>
              <span className="faint">·</span>
              <span>
                <strong>{payoutAt(maxMinor)}</strong> for a{' '}
                {maxMinor === null ? '–' : formatFaceMoney({ currency: draft.faceCurrency, amount: maxMinor.toString() })}{' '}
                card
              </span>
            </>
          )}
        </div>
        {rateMovedTooFar && (
          <div className="error-box" style={{ marginTop: 10 }} role="alert">
            <IconAlert size={16} />
            <div>
              That is {String(GIFTCARD_RATE_MAX_CHANGE_FACTOR)}× or more away from the current rate of ₦
              {giftcardRateToInput(brand!.rateNairaPerUnit)} per {brand!.faceCurrency}, the shape a
              misplaced decimal point takes. The API refuses it, so this cannot be saved. A genuine
              move that large is two smaller writes, both recorded.
            </div>
          </div>
        )}
      </div>

      <div className="grid grid--2" style={{ gap: 14 }}>
        <Field label={`Smallest face value (${draft.faceCurrency})`} htmlFor="gcb-min">
          <Input
            id="gcb-min"
            className="mono"
            inputMode="decimal"
            value={draft.minFace}
            placeholder="10"
            onChange={(e) => set('minFace', e.target.value)}
          />
        </Field>
        <Field label={`Largest face value (${draft.faceCurrency})`} htmlFor="gcb-max">
          <Input
            id="gcb-max"
            className="mono"
            inputMode="decimal"
            value={draft.maxFace}
            placeholder="200"
            onChange={(e) => set('maxFace', e.target.value)}
          />
        </Field>
      </div>

      <div className="grid grid--2" style={{ gap: 14 }}>
        <Field label="Display order" htmlFor="gcb-sort">
          <Input
            id="gcb-sort"
            className="mono"
            inputMode="numeric"
            value={draft.sortOrder}
            onChange={(e) => set('sortOrder', e.target.value)}
          />
        </Field>
        <div className="field">
          <span className="field__label">Live</span>
          <div className="gcb-live">
            <Switch
              checked={draft.active}
              label={draft.active ? 'Switch this brand off' : 'Switch this brand on'}
              onChange={(next) => set('active', next)}
            />
            <span className="muted" style={{ fontSize: 12.5 }}>
              {draft.active
                ? 'Customers can submit cards for this brand.'
                : 'Hidden from the app. Existing redemptions are unaffected.'}
            </span>
          </div>
        </div>
      </div>

      {error !== null && <ErrorBox message={error} />}
    </Modal>
  );
}

/**
 * The brand's icon, and the control that sets it.
 *
 * A cell rather than a column of buttons, because the picture *is* the affordance: an operator
 * scanning for brands that are still unfinished is looking for the empty squares, and putting
 * the upload behind them means the thing to fix and the way to fix it are the same target.
 *
 * The placeholder is the brand's own initial rather than a generic image glyph, for the reason
 * the app's own fallbacks give: a column of identical icons carries no information, and the
 * eye stops using it.
 */
function BrandIconCell({
  brand,
  canWrite,
  busy,
  onUpload,
}: {
  readonly brand: AdminGiftcardBrand;
  readonly canWrite: boolean;
  readonly busy: boolean;
  readonly onUpload: (file: File | undefined) => void;
}): JSX.Element {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const src = brandIconSrc(brand);

  return (
    <div className="gcb-icon">
      <button
        type="button"
        className="gcb-icon__btn"
        disabled={!canWrite || busy}
        title={src === null ? `Upload an icon for ${brand.name}` : `Replace ${brand.name}'s icon`}
        aria-label={src === null ? `Upload an icon for ${brand.name}` : `Replace ${brand.name}'s icon`}
        onClick={() => inputRef.current?.click()}
      >
        {src === null ? (
          <span className="gcb-icon__mark">{brand.name.slice(0, 1).toUpperCase()}</span>
        ) : (
          <img src={src} alt="" width={32} height={32} />
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        disabled={!canWrite || busy}
        onChange={(e) => {
          onUpload(e.target.files?.[0]);
          // Cleared, or choosing the same file again after a failure fires no change event.
          e.target.value = '';
        }}
      />
    </div>
  );
}

/**
 * The absolute URL for a brand's stored icon, or null.
 *
 * Composed here because the API returns a *path*: only the client knows which origin and
 * prefix it is talking to. Against the in-memory demo backend there are no stored bytes at
 * all, so the row falls back to its initial rather than pointing at a 404.
 *
 * Stamped with the brand id alone would cache the old picture through a replacement, so the
 * public route's own `?v=` is what busts it. That stamp comes from the API and is not
 * reconstructed here.
 */
function brandIconSrc(brand: AdminGiftcardBrand): string | null {
  if (USING_MOCK || brand.logoKey === null) return null;
  return `${API_ORIGIN}${API_PREFIX}/giftcards/brands/${encodeURIComponent(brand.id)}/logo`;
}

/** The raster types the contract accepts. No SVG: it can carry script. */
const ICON_MIMES: readonly string[] = ['image/png', 'image/jpeg', 'image/webp'];

/** A brand mark renders at 32 pixels. Anything approaching this is already far too much. */
const ICON_MAX_BYTES = 200 * 1024;

/** Read a File as bare base64, without the `data:` prefix the upload body does not want. */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read that file.'));
    reader.onload = () => {
      const result = String(reader.result);
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Every code on a submission, or the single legacy one.
 *
 * `cardCodes` is empty on any redemption written before the codes table existed, which is every
 * historical one. Falling back to `cardCode` keeps those reading exactly as they always did,
 * rather than showing a reviewer an empty panel over a real card they are being asked to
 * approve a payout for.
 *
 * A function rather than a value hoisted above the JSX, because `detail` is only known to be
 * non-null inside the branch that renders it.
 */
function reviewerCodes(detail: AdminRedemptionDetail): readonly string[] {
  return detail.cardCodes.length > 0 ? detail.cardCodes : [detail.cardCode];
}
